import { isBefore, subDays } from "date-fns";
import { z } from "zod";
import {
  buildPersonName,
  slugifyString,
  timestampToTranscriptTimestampFormat,
  transcriptTimestampFormatToTimestamp
} from "../helpers/utilities.js";
const recordingIdSchema = z.uuid().brand();
function asRecordingId(id) {
  return recordingIdSchema.parse(id);
}
const RecordingStatus = {
  Initialized: "initialized",
  Uploading: "uploading",
  Processing: "processing",
  Complete: "complete",
  Empty: "empty",
  Failed: "failed",
  /**
   * An upload the near-duplicate check resolved to another recording, kept
   * for review of the match and never processed or graded; see
   * {@link RecordingMetadata.near_duplicate_of}.
   */
  Duplicate: "duplicate"
};
const recordingStatuses = [
  RecordingStatus.Initialized,
  RecordingStatus.Uploading,
  RecordingStatus.Processing,
  RecordingStatus.Complete,
  RecordingStatus.Empty,
  RecordingStatus.Failed,
  RecordingStatus.Duplicate
];
function checkIsSectionApplicable(sectionGrade) {
  return !sectionGrade.is_not_applicable;
}
const RecordingSpeakerRoleType = {
  Representative: "representative",
  Customer: "customer",
  Other: "other"
};
const allRecordingSpeakerRoleTypes = [
  RecordingSpeakerRoleType.Representative,
  RecordingSpeakerRoleType.Customer,
  RecordingSpeakerRoleType.Other
];
const recordingSpeakerRoleTypeSchema = z.enum(
  allRecordingSpeakerRoleTypes
);
function mapRecordingToTrainingApp(recordingDb, mostRecentAttachmentCreatedAt) {
  return {
    id: recordingDb.id,
    company_id: recordingDb.company_id,
    title: recordingDb.title,
    description: recordingDb.description,
    recorded_at: recordingDb.recorded_at,
    created_at: recordingDb.created_at,
    updated_at: recordingDb.updated_at,
    seen_at: recordingDb.seen_at,
    duration: recordingDb.raw_transcript?.duration,
    status: recordingDb.status,
    teammate_id: recordingDb.teammate_id,
    summary: recordingDb.summary,
    raw_transcript: recordingDb.raw_transcript,
    metadata: recordingDb.metadata,
    most_recent_attachment_created_at: mostRecentAttachmentCreatedAt
  };
}
function mapRecordingToTrainingAppLight(recordingDb, mostRecentAttachmentCreatedAt) {
  return {
    id: recordingDb.id,
    company_id: recordingDb.company_id,
    title: recordingDb.title,
    description: recordingDb.description,
    recorded_at: recordingDb.recorded_at,
    created_at: recordingDb.created_at,
    updated_at: recordingDb.updated_at,
    seen_at: recordingDb.seen_at,
    duration: recordingDb.raw_transcript?.duration,
    status: recordingDb.status,
    teammate_id: recordingDb.teammate_id,
    most_recent_attachment_created_at: mostRecentAttachmentCreatedAt
  };
}
function getRecordingPathPrefix(companyId, recordingId) {
  return `${companyId}/recordings/${recordingId}`;
}
function getRecordingChunksPathPrefix(companyId, recordingId) {
  return `${getRecordingPathPrefix(companyId, recordingId)}/chunks`;
}
function getRecordingFilePath(companyId, recordingId) {
  return `${getRecordingPathPrefix(companyId, recordingId)}/audio.m4a`;
}
function getRecordingFilePathFromHash(companyId, hash) {
  return `${companyId}/recording_hashes/${hash}/audio.m4a`;
}
function getRecordingVideoFilePathFromHash(companyId, hash) {
  return `${companyId}/recording_hashes/${hash}/video.mp4`;
}
function buildRecordingAudioFingerprintPathFromHash(companyId, hash, version) {
  return `${companyId}/recording_hashes/${hash}/fingerprint.v${version}.bin`;
}
function doesRecordingStatusAllowChunkUpload(recordingStatus) {
  return ["initialized", "uploading"].includes(recordingStatus);
}
const textHighlightSchema = z.object({
  text: z.string(),
  color: z.enum(["green", "red"])
});
function checkIsRecordingContentAssessable(recording) {
  if (!recording.teammate_id || !recording.recorded_at || !recording.raw_transcript || recording.raw_transcript.is_malformed || recording.raw_transcript.is_invalid || !recording.metadata) {
    return false;
  }
  return true;
}
function checkIsAssessableRecording(recording) {
  return recording.status === "complete" && checkIsRecordingContentAssessable(recording);
}
const MIN_HUMAN_RECORDING_DURATION_SECONDS_FOR_GRADING = 20;
const MIN_VOICE_AGENT_RECORDING_DURATION_SECONDS_FOR_GRADING = 10;
function checkIsRecordingGradableForAssessment(recording, options) {
  const minDurationSeconds = options.isVoiceAgent ? MIN_VOICE_AGENT_RECORDING_DURATION_SECONDS_FOR_GRADING : MIN_HUMAN_RECORDING_DURATION_SECONDS_FOR_GRADING;
  return recording.raw_transcript.fragments.length > 0 && recording.raw_transcript.duration >= minDurationSeconds;
}
function isBackfillRecording(recording) {
  if (!recording.recorded_at) {
    return false;
  }
  const backfillThresholdDate = subDays(/* @__PURE__ */ new Date(), 1);
  return isBefore(new Date(recording.recorded_at), backfillThresholdDate);
}
function computeTalkRatio(rawTranscript) {
  if (rawTranscript.is_invalid || rawTranscript.is_malformed) {
    return void 0;
  }
  const { duration, fragments } = rawTranscript;
  const durationMs = Math.max(0, Math.floor(duration * 1e3));
  const perSpeakerMs = /* @__PURE__ */ new Map();
  for (const fragment of fragments) {
    const start = Math.max(
      0,
      Math.min(durationMs, Math.floor(fragment.start_time))
    );
    const end = Math.max(
      0,
      Math.min(durationMs, Math.floor(fragment.end_time))
    );
    if (end <= start) {
      continue;
    }
    const currentSpeakerMs = perSpeakerMs.get(fragment.speaker_id) ?? 0;
    const additionalSpeakingTimeMs = end - start;
    perSpeakerMs.set(
      fragment.speaker_id,
      currentSpeakerMs + additionalSpeakingTimeMs
    );
  }
  const totalSpeechMs = Array.from(perSpeakerMs.values()).reduce(
    (a, b) => a + b,
    0
  );
  return {
    total_speech_ms: totalSpeechMs,
    per_speaker_ms: Object.fromEntries(perSpeakerMs)
  };
}
function computeTalkRatioForRep(metadatas) {
  let totalSpeechMs = 0;
  let speakerMs = 0;
  for (const metadata of metadatas) {
    const speaker = metadata.speakers?.find(
      (speaker2) => speaker2.role_type === RecordingSpeakerRoleType.Representative
    );
    if (!speaker || !metadata.talk_ratio) {
      continue;
    }
    totalSpeechMs += metadata.talk_ratio.total_speech_ms ?? 0;
    speakerMs += metadata.talk_ratio.per_speaker_ms[speaker.id] ?? 0;
  }
  if (totalSpeechMs === 0 || speakerMs === 0) {
    return void 0;
  }
  return {
    total_speech_ms: totalSpeechMs,
    speaker_ms: speakerMs
  };
}
function buildDatedTranscripts(recordings, toolFragments, options) {
  return recordings.map(
    (recording) => buildDatedTranscript(recording, toolFragments, options)
  );
}
function buildDatedTranscript(recording, toolFragments, options) {
  const speakers = recording.metadata?.speakers || [];
  const fragments = recording.raw_transcript.fragments;
  const recordingToolFragments = toolFragments?.filter(
    (activity) => activity.recording_id === recording.id
  );
  return {
    duration: recording.raw_transcript.duration,
    text: buildRecordingTranscript(
      speakers,
      fragments,
      recordingToolFragments,
      options
    ),
    date: new Date(recording.recorded_at).toISOString(),
    speakers
  };
}
const TRANSCRIPT_SLICE_FALLBACK_TURN_THRESHOLD = 15;
const TRANSCRIPT_SLICE_FALLBACK_COVERAGE_RATIO = 0.8;
function buildRecordingTranscriptSlice(recording, toolFragments, startTimeMs, endTimeMs, options) {
  const speakers = recording.metadata?.speakers ?? [];
  const allFragments = recording.raw_transcript.fragments;
  const recordingToolFragments = toolFragments?.filter(
    (activity) => activity.recording_id === recording.id
  );
  const start = Math.min(startTimeMs, endTimeMs);
  const end = Math.max(startTimeMs, endTimeMs);
  const fragmentsInRange = allFragments.filter(
    (f) => f.end_time >= start && f.start_time <= end
  );
  const isShortCall = allFragments.length <= TRANSCRIPT_SLICE_FALLBACK_TURN_THRESHOLD;
  const coverageRatio = allFragments.length === 0 ? 0 : fragmentsInRange.length / allFragments.length;
  const expandedToFullTranscript = isShortCall || coverageRatio >= TRANSCRIPT_SLICE_FALLBACK_COVERAGE_RATIO;
  if (expandedToFullTranscript) {
    return {
      text: buildRecordingTranscript(
        speakers,
        allFragments,
        recordingToolFragments,
        options
      ),
      expandedToFullTranscript: true
    };
  }
  const toolFragmentsInRange = recordingToolFragments?.filter(
    (tf) => tf.time >= start && tf.time <= end
  );
  const sliceText = buildRecordingTranscript(
    speakers,
    fragmentsInRange,
    toolFragmentsInRange,
    {
      ...options,
      omitCallEndedMarker: true
    }
  );
  const startTs = timestampToTranscriptTimestampFormat(start);
  const endTs = timestampToTranscriptTimestampFormat(end);
  return {
    text: `(Transcript segment ${startTs} \u2013 ${endTs}; the call continues before and after this excerpt)

${sliceText}

(End of transcript segment)`,
    expandedToFullTranscript: false
  };
}
function parseTranscriptTimestampLoose(input) {
  const unwrapped = input.trim().replace(/^\[/, "").replace(/\]$/, "").trim();
  return transcriptTimestampFormatToTimestamp(`[${unwrapped}]`);
}
function buildRecordingTranscript(speakers, fragments, toolFragments, options) {
  const useSlugifiedToolNames = options?.useSlugifiedToolNames ?? false;
  const lines = fragments.map((fragment) => {
    const speaker = speakers.find(
      (speaker2) => speaker2.id === fragment.speaker_id
    );
    const speakerName = buildPersonName(speaker) || speaker?.name || fragment.speaker_id;
    const roleSuffix = options?.includeSpeakerRole && speaker?.role_type ? ` (${speaker.role_type})` : "";
    return {
      time: fragment.start_time,
      text: `${speakerName}${roleSuffix}: ${fragment.text_transcript}`
    };
  });
  if (toolFragments) {
    for (const toolFragment of toolFragments) {
      const parts = [];
      if (toolFragment.effect_text) {
        parts.push(toolFragment.effect_text);
      }
      if (toolFragment.input_text) {
        parts.push(`<tool_input>${toolFragment.input_text}</tool_input>`);
      }
      if (toolFragment.output_text) {
        parts.push(`<tool_output>${toolFragment.output_text}</tool_output>`);
      }
      const toolName = useSlugifiedToolNames ? slugifyString(toolFragment.tool_name) : toolFragment.tool_name;
      const toolLine = {
        time: toolFragment.time,
        text: parts.length > 0 ? `<tool_used name="${toolName}">${parts.join("\n")}</tool_used>` : `<tool_used name="${toolName}" />`
      };
      const insertIndex = lines.findIndex(
        (line) => line.time > toolFragment.time
      );
      if (insertIndex === -1) {
        lines.push(toolLine);
      } else {
        lines.splice(insertIndex, 0, toolLine);
      }
    }
  }
  const endFragment = fragments.at(-1);
  if (endFragment && !options?.omitCallEndedMarker) {
    lines.push({
      time: endFragment.end_time,
      text: "(Call ended)"
    });
  }
  const chunks = lines.map((line) => {
    const tsPrefix = timestampToTranscriptTimestampFormat(line.time);
    return `${tsPrefix} ${line.text}`;
  });
  return chunks.join("\n\n");
}
function buildTranscriptsWithCallTags(assessableRecordings, toolFragments, options) {
  const datedTranscripts = buildDatedTranscripts(
    assessableRecordings,
    toolFragments,
    options
  );
  const shouldOmitDate = options?.shouldOmitDate ?? false;
  const transcriptsWithTags = datedTranscripts.map((transcript) => {
    const openTag = shouldOmitDate ? "<call>" : `<call date="${transcript.date}">`;
    return `${openTag}
${transcript.text}
</call>`;
  });
  return transcriptsWithTags.join("\n\n");
}
function isTotalDurationTranscriptsBelowThreshold(datedTranscripts, minutesThreshold) {
  const totalDurationInSec = datedTranscripts.reduce((acc, datedTranscript) => {
    return acc + datedTranscript.duration;
  }, 0);
  return totalDurationInSec < minutesThreshold * 60;
}
function updateGradingValidation(grading, section_or_criteria_id, validation) {
  if (grading.section_grading) {
    const sectionGrade = grading.section_grading[section_or_criteria_id];
    if (sectionGrade) {
      return {
        ...grading,
        section_grading: {
          ...grading.section_grading,
          [section_or_criteria_id]: {
            ...sectionGrade,
            validation
          }
        }
      };
    }
  }
  const criteriaGrade = grading.criteria_grading[section_or_criteria_id];
  if (criteriaGrade) {
    return {
      ...grading,
      criteria_grading: {
        ...grading.criteria_grading,
        [section_or_criteria_id]: {
          ...criteriaGrade,
          validation
        }
      }
    };
  }
  return grading;
}
export {
  MIN_HUMAN_RECORDING_DURATION_SECONDS_FOR_GRADING,
  MIN_VOICE_AGENT_RECORDING_DURATION_SECONDS_FOR_GRADING,
  RecordingSpeakerRoleType,
  RecordingStatus,
  TRANSCRIPT_SLICE_FALLBACK_COVERAGE_RATIO,
  TRANSCRIPT_SLICE_FALLBACK_TURN_THRESHOLD,
  allRecordingSpeakerRoleTypes,
  asRecordingId,
  buildDatedTranscript,
  buildDatedTranscripts,
  buildRecordingAudioFingerprintPathFromHash,
  buildRecordingTranscript,
  buildRecordingTranscriptSlice,
  buildTranscriptsWithCallTags,
  checkIsAssessableRecording,
  checkIsRecordingContentAssessable,
  checkIsRecordingGradableForAssessment,
  checkIsSectionApplicable,
  computeTalkRatio,
  computeTalkRatioForRep,
  doesRecordingStatusAllowChunkUpload,
  getRecordingChunksPathPrefix,
  getRecordingFilePath,
  getRecordingFilePathFromHash,
  getRecordingPathPrefix,
  getRecordingVideoFilePathFromHash,
  isBackfillRecording,
  isTotalDurationTranscriptsBelowThreshold,
  mapRecordingToTrainingApp,
  mapRecordingToTrainingAppLight,
  parseTranscriptTimestampLoose,
  recordingIdSchema,
  recordingSpeakerRoleTypeSchema,
  textHighlightSchema,
  updateGradingValidation
};
//# sourceMappingURL=recording.js.map
